import { useMemo, useState } from 'react'
import { Table, DatePicker, Select, Avatar, Space, List, Tooltip, Button } from 'antd'
import { blue, gold } from '@ant-design/colors'
import { CloseCircleOutlined, EditOutlined } from '@ant-design/icons'
import DurationPicker from '../../common/DurationPicker'
import TimePicker from '../../common/TimePicker'
import { ExpandedEvent } from './createEventPayloadBuilder'
import { buildOriginalEventsMap, isEventFieldChanged, shiftedEndDate } from './schedulePreviewModel'
import { CrewMember, SchedulingWorkArea } from '../scheduleApi'
import { getInitials } from '../scheduleModel'

type Props = {
  events: ExpandedEvent[]
  onEventsChange: (events: ExpandedEvent[]) => void
  crew: CrewMember[]
  workAreas: SchedulingWorkArea[]
  cadenceWorkareaMap: Map<number, number[]>
}

const CrewCell = ({ record, crew, onUpdate }: { record: ExpandedEvent, crew: CrewMember[], onUpdate: (updates: Partial<ExpandedEvent>) => void }) => {
  const [editing, setEditing] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)
  const selectedMembers = crew.filter(c => record.crewMemberIds.includes(c.id))

  const handleCrewSelect = (ids: number[]) => {
    let newCrewLeadId = record.crewLeadId && ids.includes(record.crewLeadId)
      ? record.crewLeadId
      : null
    if (!newCrewLeadId) {
      const addedIds = ids.filter(id => !record.crewMemberIds.includes(id))
      const eligibleLead = addedIds.map(id => crew.find(c => c.id === id)).find(c => c?.userEmail)
      if (eligibleLead) {
        newCrewLeadId = eligibleLead.id
      }
    }
    onUpdate({ crewMemberIds: ids, crewLeadId: newCrewLeadId })
  }

  const removeMember = (id: number) => {
    const newIds = record.crewMemberIds.filter(mid => mid !== id)
    const newCrewLeadId = record.crewLeadId === id ? null : record.crewLeadId
    onUpdate({ crewMemberIds: newIds, crewLeadId: newCrewLeadId })
  }

  const toggleCrewLead = (id: number) => {
    onUpdate({ crewLeadId: record.crewLeadId === id ? null : id })
  }

  const handleStartEditing = () => {
    setEditing(true)
    setSelectOpen(true)
  }

  const handleDoneEditing = () => {
    setSelectOpen(false)
    setEditing(false)
  }

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedMembers.length === 0 ? (
            <span style={{ color: '#999' }}>No crew</span>
          ) : (
            <Space size={4} wrap>
              {[...selectedMembers].sort((a, b) => {
                if (a.id === record.crewLeadId) { return -1 }
                if (b.id === record.crewLeadId) { return 1 }
                return a.name.localeCompare(b.name)
              }).map(m => (
                <Space key={m.id} size={4}>
                  <Avatar
                    size="small"
                    src={m.hasStockAvatar ? undefined : m.avatar}
                    style={m.hasStockAvatar || !m.avatar ? { backgroundColor: m.color || '#d9d9d9', color: '#fff' } : undefined}
                  >
                    {getInitials(m.name)}
                  </Avatar>
                  <span style={{ fontSize: 13, fontWeight: record.crewLeadId === m.id ? 600 : 400 }}>
                    {m.name}
                  </span>
                </Space>
              ))}
            </Space>
          )}
        </div>
        <EditOutlined
          style={{ color: '#999', cursor: 'pointer', flexShrink: 0 }}
          onClick={handleStartEditing}
        />
      </div>
    )
  }

  return (
    <div>
      <Select
        showSearch
        open={selectOpen}
        onOpenChange={setSelectOpen}
        placeholder="Search and select crew members"
        optionFilterProp="label"
        style={{ width: '100%', marginBottom: 8 }}
        onChange={handleCrewSelect}
        value={record.crewMemberIds}
        allowClear
        mode="multiple"
        maxTagCount={2}
        maxTagTextLength={8}
      >
        {crew?.map((member) => (
          <Select.Option key={member.id} value={member.id} label={member.name}>
            <Space>
              <Avatar
                size="small"
                src={member.hasStockAvatar ? undefined : member.avatar}
                style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
              >
                {getInitials(member.name)}
              </Avatar>
              {member.name}
            </Space>
          </Select.Option>
        ))}
      </Select>

      {selectedMembers.length > 0 && (
        <List
          size="small"
          dataSource={selectedMembers}
          renderItem={(member) => (
            <List.Item
              style={{ flexWrap: 'wrap' }}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    size="small"
                    src={member.hasStockAvatar ? undefined : member.avatar}
                    style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                  >
                    {getInitials(member.name)}
                  </Avatar>
                }
                title={
                  <span style={{ fontWeight: record.crewLeadId === member.id ? 600 : 400 }}>
                    {member.name}
                  </span>
                }
              />
              <Space size={8}>
                {!member.userEmail ? (
                  <Tooltip title="Crew member needs an email address to be a crew lead">
                    <Button type="link" size="small" disabled style={{ padding: 0 }}>
                      Set as Crew Lead
                    </Button>
                  </Tooltip>
                ) : (
                  <a
                    onClick={() => toggleCrewLead(member.id)}
                    style={{ color: record.crewLeadId === member.id ? '#d4380d' : '#1890ff', whiteSpace: 'nowrap' }}
                  >
                    {record.crewLeadId === member.id ? 'Remove Crew Lead' : 'Set as Crew Lead'}
                  </a>
                )}
                <a onClick={() => removeMember(member.id)}>
                  <CloseCircleOutlined style={{ fontSize: 16 }} />
                </a>
              </Space>
            </List.Item>
          )}
        />
      )}

      <a onClick={handleDoneEditing} style={{ fontSize: 13, color: blue.primary }}>Done</a>
    </div>
  )
}

const changedStyle = { outline: `1px solid ${gold.primary}`, borderRadius: 4 }

const SchedulePreviewTable = ({ events, onEventsChange, crew, workAreas, cadenceWorkareaMap }: Props) => {
  // Deliberately captures only the events present on mount
  const originalEventsMap = useMemo(() => buildOriginalEventsMap(events), [])

  const updateEvent = (key: number, updates: Partial<ExpandedEvent>) => {
    onEventsChange(events.map(e => e.key === key ? { ...e, ...updates } : e))
  }

  const columns = [
    {
      title: 'Work Areas',
      key: 'workAreas',
      width: 180,
      render: (_: any, record: ExpandedEvent) => {
        const workareaIds = cadenceWorkareaMap.get(record.cadenceKey) || []
        const matchedAreas = workAreas.filter(wa => workareaIds.includes(wa.id))
        if (matchedAreas.length === 0) {
          return <span style={{ color: '#999' }}>None</span>
        }
        return matchedAreas.map(wa => wa.name).join(', ')
      },
    },
    {
      title: 'Date',
      key: 'date',
      width: 150,
      render: (_: any, record: ExpandedEvent) => (
        <DatePicker
          value={record.startDate}
          format="MMM D, YYYY"
          onChange={(newStartDate) => {
            if (newStartDate) {
              updateEvent(record.key, { startDate: newStartDate, endDate: shiftedEndDate(record.startDate, record.endDate, newStartDate) })
            }
          }}
          style={{ width: '100%', ...(isEventFieldChanged(record, 'startDate', originalEventsMap) ? changedStyle : {}) }}
        />
      ),
    },
    {
      title: 'Time',
      key: 'time',
      width: 120,
      render: (_: any, record: ExpandedEvent) => {
        if (record.isAllDay) {
          return <span style={{ color: '#999' }}>All Day</span>
        }
        return (
          <TimePicker
            value={record.startTime}
            minuteStep={5}
            onChange={(time) => updateEvent(record.key, { startTime: time })}
            style={{ width: '100%', ...(isEventFieldChanged(record, 'startTime', originalEventsMap) ? changedStyle : {}) }}
          />
        )
      },
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 90,
      render: (_: any, record: ExpandedEvent) => {
        if (record.isAllDay) {
          return null
        }
        return (
          <DurationPicker
            value={record.duration}
            increment={300}
            onChange={(duration) => updateEvent(record.key, { duration })}
            style={{ width: '100%', ...(isEventFieldChanged(record, 'duration', originalEventsMap) ? changedStyle : {}) }}
          />
        )
      },
    },
    {
      title: 'Crew',
      key: 'crew',
      width: 350,
      render: (_: any, record: ExpandedEvent) => {
        const crewChanged = isEventFieldChanged(record, 'crewMemberIds', originalEventsMap) || isEventFieldChanged(record, 'crewLeadId', originalEventsMap)
        return (
          <div style={crewChanged ? { ...changedStyle, padding: 4 } : undefined}>
            <CrewCell
              record={record}
              crew={crew}
              onUpdate={(updates) => updateEvent(record.key, updates)}
            />
          </div>
        )
      },
    },
  ]

  const sortedEvents = [...events].sort((a, b) => a.startDate.valueOf() - b.startDate.valueOf())

  return (
    <Table
      dataSource={sortedEvents}
      columns={columns}
      rowKey="key"
      pagination={false}
      size="small"
    />
  )
}

export default SchedulePreviewTable
